import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import {
    Mail,
    Trash2,
    Loader2,
    CheckCircle,
    Clock,
    XCircle,
    MoreVertical,
    Download
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { getHonorariumNotes, updateHonorariumNoteStatus, deleteHonorariumNote, getHonorariumNoteDetails, getTrainer } from '@/lib/api'
import { HonorariumPdfService } from '@/lib/honorarium-pdf-service'
import { supabase } from '@/lib/supabase'
import type { HonorariumNote, HonorariumNoteStatus } from '@/types/domain'

interface HonorariumHistoryListProps {
    trainerId: string
    isAdmin: boolean
}

const statusConfig: Record<HonorariumNoteStatus, { label: string; icon: any; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    draft: { label: 'Entwurf', icon: Clock, variant: 'outline' },
    sent: { label: 'Versendet', icon: Mail, variant: 'secondary' },
    paid: { label: 'Bezahlt', icon: CheckCircle, variant: 'default' },
    cancelled: { label: 'Storniert', icon: XCircle, variant: 'destructive' },
}

export function HonorariumHistoryList({ trainerId, isAdmin }: HonorariumHistoryListProps) {
    const queryClient = useQueryClient()

    const { data: notes, isLoading } = useQuery({
        queryKey: ['honorarium-notes', trainerId],
        queryFn: () => getHonorariumNotes(trainerId),
    })

    const downloadMutation = useMutation({
        mutationFn: async (note: HonorariumNote) => {
            const [details, trainer] = await Promise.all([
                getHonorariumNoteDetails(note.id),
                getTrainer(trainerId),
            ])
            if (!trainer) throw new Error('Trainer nicht gefunden')
            return HonorariumPdfService.generate(details, details.items, trainer)
        },
        onSuccess: () => toast.success('PDF erfolgreich generiert'),
        onError: (error) => toast.error(`Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`),
    })

    const sendEmailMutation = useMutation({
        mutationFn: async (note: HonorariumNote) => {
            const { error } = await supabase.functions.invoke('send-honorarium-email', {
                body: { noteId: note.id },
            })
            if (error) throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['honorarium-notes', trainerId] })
            toast.success('E-Mail erfolgreich versendet')
        },
        onError: (error) => toast.error(`Email-Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`),
    })

    const statusMutation = useMutation({
        mutationFn: ({ id, status }: { id: string; status: HonorariumNoteStatus }) =>
            updateHonorariumNoteStatus(id, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['honorarium-notes', trainerId] })
            toast.success('Status aktualisiert')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteHonorariumNote(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['honorarium-notes', trainerId] })
            toast.success('Honorarnote gelöscht')
        },
    })

    if (isLoading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        )
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Honorarnoten</CardTitle>
                <CardDescription>
                    Übersicht aller für diesen Trainer generierten Honorarnoten.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="border rounded-md">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Zeitraum</TableHead>
                                <TableHead className="text-right">Betrag</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Erstellt am</TableHead>
                                <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {notes?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        Noch keine Honorarnoten vorhanden.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                notes?.map((note) => {
                                    const StatusIcon = statusConfig[note.status].icon
                                    return (
                                        <TableRow key={note.id}>
                                            <TableCell className="text-sm">
                                                {format(parseISO(note.period_start), 'dd.MM.yy')} - {format(parseISO(note.period_end), 'dd.MM.yy')}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {(note.total_amount_cents / 100).toFixed(2)} €
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={statusConfig[note.status].variant} className="gap-1 font-normal">
                                                    <StatusIcon className="h-3 w-3" />
                                                    {statusConfig[note.status].label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {format(parseISO(note.created_at), 'dd.MM.yyyy', { locale: de })}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => downloadMutation.mutate(note)}
                                                        disabled={downloadMutation.isPending}
                                                    >
                                                        {downloadMutation.isPending ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Download className="h-4 w-4" />
                                                        )}
                                                    </Button>

                                                    {isAdmin && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm">
                                                                    <MoreVertical className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                                                                <DropdownMenuItem
                                                                    onClick={() => sendEmailMutation.mutate(note)}
                                                                    disabled={sendEmailMutation.isPending}
                                                                >
                                                                    {sendEmailMutation.isPending ? (
                                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <Mail className="mr-2 h-4 w-4" />
                                                                    )}
                                                                    Per Mail senden
                                                                </DropdownMenuItem>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuLabel>Status ändern</DropdownMenuLabel>
                                                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: note.id, status: 'sent' })}>
                                                                    Markieren als versendet
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: note.id, status: 'paid' })}>
                                                                    Markieren als bezahlt
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: note.id, status: 'cancelled' })}>
                                                                    Markieren als storniert
                                                                </DropdownMenuItem>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuItem
                                                                    className="text-destructive"
                                                                    onClick={() => deleteMutation.mutate(note.id)}
                                                                >
                                                                    <Trash2 className="mr-2 h-4 w-4" /> Löschen
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}
